import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { DaySection } from '@/components/plan/DaySection';
import { NextUpHero } from '@/components/plan/NextUpHero';
import { SpotifyEmbed } from '@/components/plan/SpotifyEmbed';
import { TipRotator } from '@/components/plan/TipRotator';
import { InviteButton } from '@/components/auth/InviteButton';
import { PwaInstallButton } from '@/components/pwa/PwaInstallButton';
import { TripNav } from '@/components/nav/TripNav';
import { useAuth } from '@/hooks/useAuth';
import { useTrip } from '@/hooks/useTrip';
import { useActs } from '@/hooks/useActs';
import { useFavorites } from '@/hooks/useFavorites';
import { useVotes } from '@/hooks/useVotes';
import { groupActsByDay } from '@/lib/actHelpers';
import type { Activity } from '@/api/types';
import styles from './PlanPage.module.css';

interface PlanPageProps {
  slug: string;
}

type Filter = 'all' | 'favs' | 'crew';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'Alle' },
  { id: 'favs', label: '★ Favoriten' },
  { id: 'crew', label: '👥 Crew-Plan' },
];

export default function PlanPage({ slug }: PlanPageProps) {
  const { identity, logoutMutation } = useAuth(slug);
  const tripQuery = useTrip(slug);
  const actsQuery = useActs(slug);
  const { favoritesSet } = useFavorites(slug);
  const { votes } = useVotes(slug);
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = useMemo<Activity[]>(() => {
    const all = actsQuery.data ?? [];
    if (filter === 'favs') return all.filter((a) => favoritesSet.has(a.id));
    if (filter === 'crew') return all.filter((a) => (votes[a.id] ?? []).length > 0);
    return all;
  }, [actsQuery.data, filter, favoritesSet, votes]);

  const groups = useMemo(() => groupActsByDay(filtered), [filtered]);

  const trip = tripQuery.data?.trip;
  const tripHeader = trip?.header ?? 'Trip';
  const tripDate = trip?.dateBadge;
  const tripSubtitle = trip?.subtitle;
  const kicker = [tripDate, tripSubtitle].filter(Boolean).join(' · ');

  return (
    <>
      <div className="app-shell">
        <div className={styles.page}>
          <header className={styles.header}>
            <div>
              {kicker ? <div className={styles.kicker}>{kicker}</div> : null}
              <h1 className={styles.title}>{tripHeader}</h1>
            </div>
            <Button
              variant="ghost"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              {logoutMutation.isPending ? 'Logout…' : 'Logout'}
            </Button>
          </header>

          <NextUpHero slug={slug} />

          <TipRotator slug={slug} />

          <SpotifyEmbed />

          <p className={styles.meLine}>
            Eingeloggt als <strong>{identity?.name ?? identity?.username}</strong>
          </p>

          <div className={styles.filters} role="tablist">
            {FILTERS.map((f) => {
              const count = countFor(f.id, actsQuery.data, favoritesSet, votes);
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  onClick={() => setFilter(f.id)}
                  className={`${styles.filter} ${filter === f.id ? styles.filterActive : ''}`}
                >
                  {f.label}
                  {typeof count === 'number' ? <span className={styles.count}>{count}</span> : null}
                </button>
              );
            })}
          </div>

          {actsQuery.isLoading ? (
            <div className="loading">Lade Plan…</div>
          ) : actsQuery.isError ? (
            <div className="error">Plan konnte nicht geladen werden.</div>
          ) : groups.length === 0 ? (
            <div className={styles.empty}>
              {filter === 'favs'
                ? 'Noch keine Favoriten markiert. Tippe auf das ☆-Symbol bei einem Act.'
                : filter === 'crew'
                  ? 'Noch hat niemand "Ich bin dabei" gewählt.'
                  : 'Es sind noch keine Acts angelegt.'}
            </div>
          ) : (
            groups.map((group) => (
              <DaySection key={group.date || 'no-date'} slug={slug} group={group} />
            ))
          )}

          <InviteButton slug={slug} />

          <PwaInstallButton />
        </div>
      </div>
      <TripNav />
    </>
  );
}

function countFor(
  filter: Filter,
  acts: Activity[] | undefined,
  favs: Set<string>,
  votes: ReturnType<typeof useVotes>['votes'],
): number | undefined {
  if (!acts) return undefined;
  if (filter === 'favs') return acts.filter((a) => favs.has(a.id)).length;
  if (filter === 'crew') return acts.filter((a) => (votes[a.id] ?? []).length > 0).length;
  return acts.length;
}
